import app from 'flarum/common/app';
import Component from 'flarum/common/Component';
import Avatar from 'flarum/common/components/Avatar';
import humanTime from 'flarum/common/utils/humanTime';
import extractText from 'flarum/common/utils/extractText';
import getBadgeSvg, { getBadgeSize } from '../utils/getBadgeSvg';
import { resolveTierForUser, getTierColor } from '../utils/tiers';
/**
 * Rich popover for the verified badge — opens on hover/focus via CSS.
 *
 * The popover content is tier-aware: header label, description and the
 * "Learn more" link all come from the configured tier definition. The
 * anchor is the badge element itself, so the absolutely-positioned
 * popover panel can centre on it cleanly.
 */
export default class VerifiedPopover extends Component {
  view() {
    const {
      user
    } = this.attrs;
    if (!user || !user.isVerified || !user.isVerified()) return null;
    const tier = resolveTierForUser(user);
    const color = getTierColor(tier);
    const size = this.attrs.size || getBadgeSize();
    const badgeStyle = {
      '--verified-size': size
    };
    if (color) badgeStyle.color = color;
    const verifiedAt = user.verifiedAt && user.verifiedAt();
    const ariaLabel = tier && tier.label ? tier.label : extractText(app.translator.trans('ramon-verified.lib.tooltip'));

    // The header sentence is the tier description (e.g. "Conta de
    // <strong>organização verificada</strong>."), allowed to carry sanitised
    // <strong>/<em> markup so admins keep the colored-bold visual treatment
    // the previous design had. When no description is configured for a tier,
    // we fall back to the universal "identidade verificada" translation so
    // the popover never renders empty.
    const tierDescription = tier && tier.description ? tier.description : null;
    const headline = tierDescription ? m.trust(tierDescription) : app.translator.trans('ramon-verified.lib.popover.headline');
    const learnMoreUrl = tier && tier.learnMoreUrl ? tier.learnMoreUrl : '';
    const tierClass = tier ? `VerifiedBadge--tier-${tier.id}` : '';

    // Pass the resolved tier color down through a CSS variable so every
    // accent inside the popover (header icon, <strong> in the headline,
    // and the badge in the anchor) tints together. CSS falls back to the
    // theme primary when --tier-color isn't set (no tier configured / no
    // color on the tier).
    const popoverStyle = {};
    if (color) popoverStyle['--tier-color'] = color;
    return m("span", {
      className: "VerifiedPopover-anchor",
      "data-tier": tier ? tier.id : undefined,
      style: popoverStyle
    }, m("span", {
      className: 'VerifiedBadge VerifiedBadge--inAnchor ' + tierClass,
      style: badgeStyle,
      role: "img",
      "aria-label": ariaLabel,
      tabIndex: 0
    }, m.trust(getBadgeSvg())), m("span", {
      className: "VerifiedPopover",
      role: "tooltip"
    }, m("span", {
      className: "VerifiedPopover-arrow",
      "aria-hidden": "true"
    }), m("span", {
      className: "VerifiedPopover-header"
    }, m("span", {
      className: "VerifiedPopover-headerIcon"
    }, m.trust(getBadgeSvg())), m("span", {
      className: "VerifiedPopover-headerText"
    }, headline, learnMoreUrl && m('[', null, ' ', m("a", {
      className: "VerifiedPopover-learnMore",
      href: learnMoreUrl,
      target: "_blank",
      rel: "noopener noreferrer"
      // Inline color as a final tiebreaker against Flarum
      // core's generic `a` rules which can win on specificity
      // in some chrome contexts (post bodies, etc).
      ,
      style: color ? {
        color
      } : undefined
    }, app.translator.trans('ramon-verified.lib.popover.learn_more'))))), m("span", {
      className: "VerifiedPopover-body"
    }, m("span", {
      className: "VerifiedPopover-user"
    }, m("span", {
      className: "VerifiedPopover-avatar"
    }, m(Avatar, {
      user: user
    })), m("span", {
      className: "VerifiedPopover-userText"
    }, m("span", {
      className: "VerifiedPopover-username"
    }, user.username()), m("span", {
      className: "VerifiedPopover-displayName"
    }, user.displayName()))), m("span", {
      className: "VerifiedPopover-meta"
    }, verifiedAt ? app.translator.trans('ramon-verified.lib.popover.verified_on', {
      date: extractText(humanTime(verifiedAt))
    }) : app.translator.trans('ramon-verified.lib.popover.verified_no_date')))));
  }
}
flarum.reg.add('ramon-verified', 'common/components/VerifiedPopover', VerifiedPopover);